import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { Box, FlexBox } from '@sternum/styled-system';
import { Text12M } from '@sternum/ui';
import { Icon } from '@sternum/web/components';
import { ThemeProvider } from '@sternum/web/components/theme/main';
import { routes } from '@sternum/web/config/routes';
import { useMenu } from '@sternum/web/features/appContainerWithMenu/hooks';
import { useCheckPermission, useGeneratePathWithCurrentClient } from '@sternum/web/features/auth/hooks';
import { AllPermissionActions } from '@sternum/web/features/auth/models';
import { useCreateDashboardModal } from '@sternum/web/features/dashboard/components';
import { useDashboards } from '@sternum/web/features/dashboard/hooks';
import { namespaces } from '@sternum/web/services/i18n';

import { MenuSection } from '../MenuSection';

import { DashboardsList } from './DashboardsList';
import { EmptyState } from './EmptyState';
import { LoadingState } from './LoadingState';
import { NoSearchResultsState } from './NoSearchResultsState';
import { SearchInput } from './SearchInput';

import {
  AddDashboardButton,
  CategoryTitle,
  CloseButton,
  MenuBottomContainer,
  MenuContent,
  MenuHeader,
  MenuHeaderInner,
  MenuStyled,
  MenuWrapper,
} from './DashboardsMenu.styles';

export function DashboardsMenu() {
  const navigate = useNavigate();
  const generatePath = useGeneratePathWithCurrentClient();
  const { t } = useTranslation(namespaces.appContainerWithMenu);
  const { can } = useCheckPermission();
  const { isDashboardMenuVisible, hideDashboardMenu } = useMenu();
  const [searchInput, setSearchInput] = useState('');

  const { dashboards, isFetching: areDashboardsLoading } = useDashboards(
    {
      search: searchInput,
    },
    {
      enabled: isDashboardMenuVisible,
      keepPreviousData: true,
    },
  );

  const { CreateDashboardModal, showCreateDashboardModal } = useCreateDashboardModal();

  const isEmptyContent = !dashboards.length;

  const handleCreateDashboardClick = async () => {
    await showCreateDashboardModal();
  };

  const renderDashboardsList = () => {
    if (searchInput.length > 0) {
      return <DashboardsList title={t('searchResultsTitle')} list={dashboards} />;
    }

    if (dashboards.length > 0) {
      return <DashboardsList title={t('customDashboardsTitle')} list={dashboards} />;
    }
  };

  const renderContent = () => {
    const addDashboardButton = can(AllPermissionActions.DashboardCreate) && (
      <Box py={6}>
        <MenuSection>
          <AddDashboardButton
            variant="text-secondary"
            leftIcon={<Icon type="PlusIcon" />}
            onClick={handleCreateDashboardClick}
          >
            <Text12M>{t('addDashboardButton')}</Text12M>
          </AddDashboardButton>
        </MenuSection>
      </Box>
    );

    if (areDashboardsLoading) {
      return (
        <>
          {addDashboardButton}

          <Box flex={1}>
            <LoadingState />
          </Box>
        </>
      );
    }

    if (isEmptyContent && searchInput.length > 0) {
      return (
        <>
          {addDashboardButton}

          <FlexBox flex={1}>
            <NoSearchResultsState />
          </FlexBox>
        </>
      );
    }

    if (isEmptyContent) {
      return (
        <FlexBox flex={1}>
          {can(AllPermissionActions.DashboardCreate) && <EmptyState onAddDashboard={handleCreateDashboardClick} />}
        </FlexBox>
      );
    }

    return (
      <>
        {addDashboardButton}
        <MenuContent>{renderDashboardsList()}</MenuContent>
      </>
    );
  };

  return (
    <>
      <ThemeProvider mode="Dark">
        <MenuWrapper aria-expanded={isDashboardMenuVisible}>
          {/* eslint-disable-next-line jsx-a11y/role-supports-aria-props */}
          <MenuStyled
            role="presentation"
            aria-label="Dashboards submenu container"
            aria-expanded={isDashboardMenuVisible}
          >
            <MenuHeader>
              <MenuHeaderInner>
                <CategoryTitle role="heading" aria-label="category title">
                  {t('menuItemDashboards')}
                </CategoryTitle>
              </MenuHeaderInner>

              <CloseButton aria-label="close dashboards submenu" size="big" onClick={hideDashboardMenu}>
                <Icon type="ChevronLeftIcon" width={12} />
              </CloseButton>
            </MenuHeader>

            {renderContent()}

            <MenuBottomContainer>
              <SearchInput value={searchInput} onChange={setSearchInput} />
            </MenuBottomContainer>
          </MenuStyled>
        </MenuWrapper>
      </ThemeProvider>

      <CreateDashboardModal
        onSuccess={(dashboardId) => {
          navigate(generatePath(routes.dashboard.fullDashboard.absolute, { dashboardId }));
        }}
      />
    </>
  );
}
